import React, { useContext } from 'react';
import { NavLink, Outlet, Link as RouterLink } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { Box, Typography, Button, Container, Chip } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import GavelIcon from '@mui/icons-material/Gavel';
import PriceChangeIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import { brand, glass } from '../theme';

const NAV_ITEMS = [
    { to: '/panel', label: 'نمای کلی', icon: <DashboardIcon fontSize="small" />, end: true },
    { to: '/panel/providers', label: 'تایید همیاران', icon: <VerifiedUserIcon fontSize="small" /> },
    { to: '/panel/requests', label: 'درخواست‌ها', icon: <ListAltIcon fontSize="small" /> },
    { to: '/panel/contracts', label: 'قراردادها', icon: <GavelIcon fontSize="small" /> },
    { to: '/panel/services', label: 'خدمات و قیمت‌گذاری', icon: <PriceChangeIcon fontSize="small" /> },
    { to: '/panel/payouts', label: 'تسویه با همیاران', icon: <AccountBalanceWalletIcon fontSize="small" /> },
    { to: '/panel/settings', label: 'تنظیمات مالی', icon: <SettingsIcon fontSize="small" /> },
];

function AdminLayout() {
    const { user, logoutUser } = useContext(AuthContext);

    if (!user?.is_staff) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>دسترسی غیرمجاز</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    این بخش مخصوص مدیران پلتفرم است. اگر فکر می‌کنید باید دسترسی داشته باشید، دوباره وارد شوید.
                </Typography>
                <Button component={RouterLink} to="/" variant="contained">بازگشت به صفحه‌ی اصلی</Button>
            </Container>
        );
    }

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', background: glass.canvasGradient }}>
            {/* نوار ناوبری موبایل — چیپ‌های شیشه‌ای قابل اسکرول افقی */}
            <Box
                sx={{
                    display: { xs: 'flex', md: 'none' }, position: 'sticky', top: 0, zIndex: 5,
                    gap: 1, p: 1.5, overflowX: 'auto', ...glass.darkCard, borderRadius: 0,
                }}
            >
                {NAV_ITEMS.map((item) => (
                    <Chip
                        key={item.to} component={NavLink} to={item.to} end={item.end}
                        icon={item.icon} label={item.label} clickable
                        sx={{
                            color: brand.heroText, flexShrink: 0,
                            '&.active': { bgcolor: 'rgba(222,156,76,0.28)', fontWeight: 700 },
                            '& .MuiChip-icon': { color: brand.heroText },
                        }}
                    />
                ))}
            </Box>

            <Box
                component="nav"
                sx={{
                    width: 260, flexShrink: 0, m: 2, p: 2.5, position: 'sticky', top: 16,
                    height: 'calc(100vh - 32px)', color: brand.heroText,
                    display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
                    ...glass.darkCard,
                }}
            >
                <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', mb: 0.5 }}>الو یارا</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(245,243,234,0.65)', mb: 3 }}>پنل مدیریت</Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1, overflowY: 'auto' }}>
                    {NAV_ITEMS.map((item) => (
                        <Box
                            key={item.to}
                            component={NavLink}
                            to={item.to}
                            end={item.end}
                            style={({ isActive }) => ({
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '10px 14px', borderRadius: 12, textDecoration: 'none',
                                color: brand.heroText,
                                background: isActive ? 'rgba(222,156,76,0.22)' : 'transparent',
                                fontWeight: isActive ? 700 : 500,
                                transition: 'background 0.15s ease',
                            })}
                        >
                            {item.icon}
                            <span>{item.label}</span>
                        </Box>
                    ))}
                </Box>

                <Button
                    component={RouterLink} to="/" startIcon={<HomeIcon />}
                    sx={{ color: brand.heroText, justifyContent: 'flex-start', mb: 1 }}
                >
                    بازگشت به سایت
                </Button>
                <Button
                    onClick={logoutUser} startIcon={<LogoutIcon />}
                    sx={{ color: brand.accent, justifyContent: 'flex-start' }}
                >
                    خروج
                </Button>
            </Box>

            <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, maxWidth: '100%', overflow: 'auto' }}>
                <Outlet />
            </Box>
        </Box>
    );
}

export default AdminLayout;
